'use client';

import { useEffect, useId, useRef, useState } from 'react';

// Shared form-control primitives for the dashboard. ONE source of truth for
// the switch toggle, select, slider, text/number fields, and section
// headings — consumed by `FieldRow`/`DraftFields`
// (components/use-auto-save-settings.js) and the panel editors so the form
// vocabularies stay in sync.
//
// Most controls accept a `layout`:
//   'row'     — bold label (+ optional gray description) on the left, control
//               on the right. The page-settings idiom.
//   'stacked' — small gray label above a full-width control. The alerts-editor
//               idiom.
//   'inline'  — (Toggle only) switch + label on one line, for compact pairs.

// Stacked-layout control label. WHITE (not grey) — these are sub-headings, and
// grey is reserved for the explanation/hint text below a control. `mb-1`
// spacing matches the prior alerts-form labels.
const STACKED_LABEL = 'block text-xs font-semibold text-gum-black dark:text-white mb-1';

// ─── Info (ⓘ tooltip) ──────────────────────────────────────────
// A small "i" affordance that reveals a help bubble. Convention: inline text
// teaches the CONCEPT; the ⓘ explains how a CONTROL behaves (the mechanics), so
// the form stays uncluttered. Desktop reveals on hover or keyboard-focus (pure
// CSS); touch taps to toggle, with an outside-tap catcher + Escape to dismiss
// (the open-state/backdrop pattern from levels-mixer). The bubble is
// pointer-events-none so it never blocks the control beneath it. `onBlur` resets
// the tapped-open state so a keyboard Enter→Tab-away can't leave it stuck open.
// `children`/`label` are developer-authored help text — React escapes them, but
// keep them static: never pass raw HTML or untrusted/user-controlled content.
export function Info({ children, label = 'More info' }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const tipId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.blur();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <span className="group relative inline-flex align-middle">
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={tipId}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gum-gray-400 text-[10px] font-bold leading-none text-gum-gray-500 hover:border-gum-pink hover:text-gum-pink focus:outline-none focus-visible:border-gum-pink focus-visible:text-gum-pink dark:border-gum-gray-600 dark:text-gum-gray-400"
      >
        i
      </button>
      {/* outside-tap/click catcher — only mounted while tapped open (touch dismissal) */}
      {open && <span className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOpen(false)} />}
      <span
        id={tipId}
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 top-6 z-50 w-72 -translate-x-1/2 rounded-lg border border-gum-gray-300 bg-gum-white px-3 py-2 text-[11px] font-normal normal-case leading-relaxed tracking-normal text-gum-gray-600 shadow-xl dark:border-gum-gray-700 dark:bg-gum-gray-900 dark:text-gum-gray-300 ${
          open ? 'block' : 'hidden group-hover:block group-focus-within:block'
        }`}
      >
        {children}
      </span>
    </span>
  );
}

// ─── Toggle (switch) ───────────────────────────────────────────
// The pink switch. Owns the negate so call sites pass `checked`/`onChange`
// without flipping the value themselves.
export function Toggle({ label, description, checked, onChange, layout = 'row', ariaLabel, disabled = false }) {
  const sw = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      // Derive an accessible name from a string label; callers passing a
      // node label (or a bare switch) should set `ariaLabel` explicitly.
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${checked ? 'bg-gum-pink' : 'bg-gum-gray-300 dark:bg-gum-gray-700'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform ${
          checked ? 'translate-x-5 bg-black' : 'translate-x-0 bg-gum-gray-500'
        }`}
      />
    </button>
  );

  if (layout === 'inline') {
    return (
      <div className="flex items-center gap-2">
        {sw}
        {label && <span className="text-sm text-gum-black dark:text-white select-none">{label}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="min-w-0 flex-1">
        {label && <div className="font-medium text-sm">{label}</div>}
        {description && <div className="text-xs text-gum-gray-500 mt-0.5 whitespace-pre-line">{description}</div>}
      </div>
      {sw}
    </div>
  );
}

// ─── Select (chevron dropdown) ─────────────────────────────────
export function Select({ label, description, value, onChange, options, layout = 'stacked' }) {
  const chevron = (
    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gum-pink">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
        <path d="M0 2l5 6 5-6z" />
      </svg>
    </div>
  );
  const opts = options.map((opt) => (
    <option key={opt.value} value={opt.value} className="bg-white dark:bg-gum-gray-800 text-gum-black dark:text-white">
      {opt.label}
    </option>
  ));

  if (layout === 'row') {
    return (
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">{label}</div>
          {description && <div className="text-xs text-gum-gray-500 mt-0.5">{description}</div>}
        </div>
        <div className="relative shrink-0">
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="bg-transparent border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-lg px-3 py-2 pr-8 text-sm outline-none appearance-none cursor-pointer focus:border-gum-pink"
          >
            {opts}
          </select>
          {chevron}
        </div>
      </div>
    );
  }

  // stacked
  return (
    <div>
      {label && <label className={STACKED_LABEL}>{label}</label>}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-lg px-3 py-2 pr-8 text-sm outline-none appearance-none cursor-pointer focus:border-gum-pink"
        >
          {opts}
        </select>
        {chevron}
      </div>
    </div>
  );
}

// ─── Slider (range) ────────────────────────────────────────────
// `row` shows a medium label + pink mono value (page-settings style);
// `stacked` shows a small gray label + gray value (alerts style).
export function Slider({
  label,
  info,
  description,
  value,
  onChange,
  min,
  max,
  step,
  unit = '',
  formatValue,
  disabled = false,
  className = '',
  layout = 'stacked',
}) {
  const display = formatValue ? formatValue(value) : `${value}${unit}`;
  const input = (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      // parseFloat (not Number) so a momentarily-empty value yields NaN rather
      // than 0 — matches the original page-settings slider behavior.
      onChange={(e) => onChange(parseFloat(e.target.value))}
      disabled={disabled}
      className={
        layout === 'row'
          ? 'w-full accent-gum-pink h-1 disabled:cursor-not-allowed'
          : 'w-full accent-gum-pink disabled:cursor-not-allowed'
      }
    />
  );

  if (layout === 'row') {
    return (
      <div className={`${className} ${disabled ? 'opacity-40' : ''}`.trim()}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center font-medium text-sm">
            {label}
            {info && <Info>{info}</Info>}
          </div>
          <span className="text-sm font-mono font-bold text-gum-pink">{display}</span>
        </div>
        {description && <div className="text-xs text-gum-gray-500 mb-3">{description}</div>}
        {input}
      </div>
    );
  }

  // stacked
  return (
    <div className={`${className} ${disabled ? 'opacity-40' : ''}`.trim()}>
      <div className="flex items-center justify-between mb-1">
        <label className="flex items-center text-xs font-semibold text-gum-black dark:text-white">
          {label}
          {info && <Info>{info}</Info>}
        </label>
        <span className="text-xs text-gum-gray-400">{display}</span>
      </div>
      {input}
    </div>
  );
}

// ─── NumberField ───────────────────────────────────────────────
// `onBlur` (optional) fires when focus leaves — lets an auto-saving panel flush
// its debounce so a half-typed number commits promptly instead of waiting it
// out. No behavior change when omitted (React ignores an undefined handler).
export function NumberField({ label, description, value, onChange, onBlur, min, max }) {
  return (
    <div>
      <div className="font-medium text-sm mb-1">{label}</div>
      {description && <div className="text-xs text-gum-gray-500 mb-3">{description}</div>}
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        onBlur={onBlur}
        className="w-32 bg-transparent border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-gum-pink focus:ring-1 focus:ring-gum-pink outline-none"
      />
    </div>
  );
}

// ─── TextField ─────────────────────────────────────────────────
// `description` renders above the input (page-settings style); `hint` renders
// below it (alerts style). Both are gray.
export function TextField({
  label,
  description,
  value,
  onChange,
  maxLength,
  placeholder,
  hint,
  layout = 'stacked',
  disabled = false,
}) {
  return (
    <div>
      {label &&
        (layout === 'row' ? (
          <div className="font-medium text-sm mb-1">{label}</div>
        ) : (
          <label className={STACKED_LABEL}>{label}</label>
        ))}
      {description && <div className="text-xs text-gum-gray-500 mb-3">{description}</div>}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder || ''}
        disabled={disabled}
        className={`w-full bg-transparent border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-lg px-3 py-2 text-sm focus:border-gum-pink focus:ring-1 focus:ring-gum-pink outline-none${
          disabled ? ' opacity-50 cursor-not-allowed' : ''
        }`}
      />
      {hint && <p className="text-[10px] text-gum-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

// ─── TextArea (auto-growing) ───────────────────────────────────
// Multi-line free-text that line-wraps and grows with its content (instead of
// scrolling sideways), up to maxLength. Mirrors TextField's API + styling;
// used for longer fields like the Link-in-Bio bio. Auto-grow re-runs on every
// value change — typing AND programmatic hydration from a fetch.
export function TextArea({ label, description, value, onChange, maxLength, placeholder, hint, disabled = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to 'auto' first so the box can SHRINK when text is removed, then
    // grow to fit. useEffect (not layout) keeps SSR warning-free; the value
    // hydrates async, so there's no visible first-paint jump.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <div>
      {label && <label className={STACKED_LABEL}>{label}</label>}
      {description && <div className="text-xs text-gum-gray-500 mb-3">{description}</div>}
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder || ''}
        disabled={disabled}
        className={`w-full resize-none overflow-hidden bg-transparent border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-lg px-3 py-2 text-sm leading-relaxed focus:border-gum-pink focus:ring-1 focus:ring-gum-pink outline-none${
          disabled ? ' opacity-50 cursor-not-allowed' : ''
        }`}
      />
      {hint && <p className="text-[10px] text-gum-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

// ─── FieldSection ──────────────────────────────────────────────
// Unified section wrapper: bold-white title + gray description + children.
// `bordered` wraps it in the card chrome the alerts editor uses.
export function FieldSection({ title, description, children, bordered = false, largeTitle = false }) {
  const inner = (
    <>
      {title && (
        <div
          className={`${largeTitle ? 'text-base font-semibold' : 'text-sm font-bold'} text-gum-black dark:text-white`}
        >
          {title}
        </div>
      )}
      {description && <div className="text-xs text-gum-gray-500 mt-0.5">{description}</div>}
      <div className={title || description ? 'space-y-3 mt-3' : 'space-y-3'}>{children}</div>
    </>
  );
  if (bordered) {
    return <div className="border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-xl p-4">{inner}</div>;
  }
  return <div>{inner}</div>;
}

// ─── PanelHeader ───────────────────────────────────────────────
// Top-level panel title + subtitle.
export function PanelHeader({ title, description }) {
  return (
    <div className="mb-2">
      <h2 className="text-lg font-bold text-gum-black dark:text-white">{title}</h2>
      {description && <p className="text-sm text-gum-gray-500 mt-1">{description}</p>}
    </div>
  );
}

// ─── Eyebrow + Card (section design-language) ──────────────────
// The card is the section boundary — its raised surface does the grouping, so
// the label inside drops to a quiet uppercase EYEBROW (+ a pink tick) instead
// of competing as another bold header. Dual-theme: a white card raised on the
// gum-gray-100 page (light) / a gum-gray-800 card raised on the gum-gray-950
// page (dark). `accent` is the ONE reserved pink card per page — for outputs
// the streamer puts on stream / shares (overlay URLs, Links Hub URL, replay).
// One pill shape for compact row actions ("Set up →", Open/Close, "Customize")
// — shared by the setup checklist and the overlay tray's Chat style cards so
// the two surfaces can't drift (promoted from onboarding-checklist.js,
// 2026-08-05). The min-width keeps different labels the same size.
export const ACTION_PILL =
  'inline-flex min-w-[4.75rem] items-center justify-center whitespace-nowrap rounded-full border border-gum-pink/30 bg-gum-pink/10 px-2.5 py-1 text-[11px] font-bold text-gum-pink hover:bg-gum-pink/20';

export function Eyebrow({ children, tick = true }) {
  return (
    <div className="flex items-center gap-2">
      {tick && <span className="h-3 w-1 rounded-full bg-gum-pink" />}
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gum-black dark:text-white">
        {children}
      </span>
    </div>
  );
}

export function Card({ eyebrow, title, desc, children, aside, accent = false, compact = false, className = '' }) {
  const pad = compact ? 'p-3.5' : 'p-5 sm:p-6';
  const tone = accent
    ? 'border-gum-pink/30 bg-gum-pink/[0.05] dark:border-gum-pink/25 dark:bg-gum-pink/[0.06]'
    : 'border-gum-gray-300 dark:border-gum-gray-700 bg-gum-white dark:bg-gum-gray-800';
  const hasHeader = eyebrow || title || desc || aside;
  return (
    <section
      className={`rounded-2xl border ${tone} ${pad} shadow-sm dark:shadow-[0_10px_30px_-18px_rgba(0,0,0,0.8)] ${className}`.trim()}
    >
      {hasHeader && (
        <div className={`flex items-start justify-between gap-3 ${children ? 'mb-4' : ''}`}>
          <div className="min-w-0">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {title && <h2 className="mt-2 text-[15px] font-semibold text-gum-black dark:text-white">{title}</h2>}
            {desc && <p className="mt-1 text-xs leading-relaxed text-gum-gray-500">{desc}</p>}
          </div>
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

// Collapsible disclosure — a quiet header button + rotating chevron that
// reveals its children. Folds a heavy/optional block (e.g. the alert chrome
// controls) so the common path stays short. Shared so panels don't re-roll the
// inline pattern. `defaultOpen` seeds the initial state (re-evaluated on remount
// — key the Disclosure by the edited row's id to reset per row).
export function Disclosure({ label, defaultOpen = false, accent = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 text-sm font-medium text-gum-black dark:text-white"
      >
        <span>{label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`${accent ? 'text-gum-pink' : 'text-gum-gray-500'} transition-transform ${open ? 'rotate-90' : ''}`}
        >
          <path d="M4 2l4 4-4 4" />
        </svg>
      </button>
      <div id={panelId} hidden={!open} className="mt-4">
        {children}
      </div>
    </div>
  );
}

// ─── Button ─────────────────────────────────────────────────────
// The four button voices used across the dashboard, canonicalized from the
// most-repeated existing conventions (primary/secondary/danger) plus the
// quiet text-only row-action style (ghost). `tone="danger"` on a `ghost`
// button gives a destructive affordance without a solid button's visual
// weight — the row-level "Delete"/"Remove" idiom. `iconOnly` drops visible
// text; callers passing it should also pass `aria-label` for accessibility.
export function Button({
  variant = 'primary',
  tone,
  size = 'md',
  icon,
  iconOnly = false,
  loading = false,
  loadingText,
  fullWidth = false,
  className = '',
  disabled = false,
  type = 'button',
  children,
  ...rest
}) {
  const sizeClasses = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  const variantClasses = {
    primary: 'bg-gum-pink text-gum-black font-bold hover:brightness-110 active:scale-[0.98]',
    secondary:
      'border border-gum-gray-300 dark:border-gum-gray-700 text-gum-gray-700 dark:text-gum-gray-200 hover:bg-gum-gray-100 dark:hover:bg-gum-gray-800 font-semibold',
    danger: 'bg-red-600 text-white font-semibold hover:bg-red-500',
    ghost:
      tone === 'danger'
        ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300 font-medium'
        : 'text-gum-gray-400 hover:bg-gum-pink/10 hover:text-gum-pink font-medium',
  }[variant];

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg transition-transform disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses} ${variantClasses} ${
        fullWidth ? 'w-full' : ''
      } ${className}`.trim()}
      {...rest}
    >
      {icon}
      {!iconOnly && (loading && loadingText ? loadingText : children)}
    </button>
  );
}

// ─── EmptyState ─────────────────────────────────────────────────
// "Nothing here yet" placeholder — the tinted box already shared byte-for-byte
// by the mod-tools lists, canonicalized as one component so every other list
// (filters, pronunciation, commands, donations, media pickers) converges on
// the same treatment instead of independently reinventing padding/tone.
export function EmptyState({ children, icon, className = '' }) {
  return (
    <div
      className={`rounded-xl border border-gum-gray-300 bg-gum-gray-200/50 p-6 text-center text-sm text-gum-gray-500 dark:border-gum-gray-700 dark:bg-gum-gray-800/50 ${className}`.trim()}
    >
      {icon}
      {children}
    </div>
  );
}

// ─── Pill ────────────────────────────────────────────────────────
// Small rounded status/label chip. Connection surfaces render it with the
// tone/label descriptors from lib/connection-status.js (the one derivation
// table — CONNECTION-PRD §8). `dotClassName` renders a small leading pulsing
// dot; omit it for a plain text pill.
const PILL_TONES = {
  good: 'bg-emerald-500/15 text-emerald-400',
  warn: 'bg-yellow-500/15 text-yellow-400',
  bad: 'bg-red-500/15 text-red-400',
  info: 'bg-sky-500/15 text-sky-400',
  neutral: 'bg-gum-gray-500/15 text-gum-gray-400',
};

export function Pill({ tone, dotClassName, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm whitespace-nowrap ${PILL_TONES[tone]}`}
    >
      {dotClassName && <span className={`inline-block w-1.5 h-1.5 rounded-full animate-pulse ${dotClassName}`} />}
      {children}
    </span>
  );
}
